import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { colorTransition, focusRing, iconSlot } from "./base/mixins";
import Tooltip from "./Tooltip";

export interface ModalProps {
	open: boolean;
	onClose: () => void;
	title: React.ReactNode;
	description?: React.ReactNode;
	/** Right-aligned action row. The primary action is a filled button, auto-width. */
	footer?: React.ReactNode;
	/**
	 * Control placed on the title line, immediately after the title — for an
	 * action on what the dialog SHOWS, such as reloading it. It reads as part of
	 * the heading, which is why it sits there and not over by the close button:
	 * that corner is dismiss chrome, and a second control beside it invites the
	 * wrong one to be clicked. Anything that ends the dialog belongs in `footer`.
	 */
	headerAction?: React.ReactNode;
	/**
	 * Adds a minimize button left of the close button: puts the dialog away
	 * while what it shows keeps running in the background (the Navigator's Live
	 * View keeps its streaming session). The exception to "nothing beside the
	 * close button": both put the dialog away, so clicking the wrong one costs
	 * nothing worse than a reopen. Needs `minimizeLabel` for its accessible name.
	 *
	 * With it, Escape and a click on the scrim minimize rather than close: they
	 * are the easy-to-hit ways out, and should be the ones that lose nothing.
	 * Closing is then the close button alone.
	 */
	onMinimize?: () => void;
	minimizeLabel?: string;
	/** Hover tooltips explaining the two header buttons — worth giving when
	 *  minimize and close differ in a way the icons cannot say. */
	minimizeTooltip?: React.ReactNode;
	closeTooltip?: React.ReactNode;
	/**
	 * `default` — 776px, 32px padding: dialogs with real content (members,
	 * sharing, a form of several fields).
	 * `compact` — 480px, 24px padding: one field or one question - rename,
	 * create with a name, confirm a deletion. DESIGN_SYSTEM.md §8 Modal.
	 * `viewport` — fills the window up to the scrim's padding, 24px padding;
	 * the content area takes the full remaining height. For a live view that
	 * wants all the screen it can get (the Navigator's Live View).
	 */
	size?: "default" | "compact" | "viewport";
	children?: React.ReactNode;
	className?: string;
}

/**
 * DESIGN_SYSTEM.md §8: 776px, 12px radius, 32px padding, and `--shadow-modal` —
 * the only shadow in the system. Scrim is rgba(31,31,31,0.40). The compact size
 * is 480px with 24px padding; the viewport size fills the window inside the
 * scrim's padding, with 24px padding.
 */
const Modal = ({
	open,
	onClose,
	title,
	description,
	footer,
	headerAction,
	onMinimize,
	minimizeLabel,
	minimizeTooltip,
	closeTooltip,
	size = "default",
	children,
	className,
}: ModalProps) => {
	const compact = size === "compact";
	const viewport = size === "viewport";
	const dialogRef = useRef<HTMLDivElement>(null);

	/* The latest onClose, read at the moment Escape is pressed. Callers pass it
	   inline, so it is a new function on every render of theirs — and it used to
	   be a dependency of the effect below, which therefore re-ran on every
	   render and pulled focus back onto the dialog. In a dialog with an input
	   that meant every keystroke (a re-render) threw the caret out of the field. */
	const dismiss = onMinimize ?? onClose;
	const onCloseRef = useRef(dismiss);
	onCloseRef.current = dismiss;

	useEffect(() => {
		if (!open) return undefined;
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") onCloseRef.current();
		};
		document.addEventListener("keydown", handleKeyDown);
		// Focus moves into the dialog so the ring has somewhere to land, and so the
		// tab order does not start back on the page behind the scrim — but only if
		// it is not in there already: an autoFocus field inside has taken it during
		// the same commit, and this effect runs after that.
		const dialog = dialogRef.current;
		if (dialog && !dialog.contains(document.activeElement)) dialog.focus();
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [open]);

	if (!open) return null;

	return createPortal(
		<Scrim onMouseDown={(event) => event.target === event.currentTarget && dismiss()}>
			<Dialog
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-label={typeof title === "string" ? title : undefined}
				tabIndex={-1}
				className={className}
				$compact={compact}
				$viewport={viewport}
			>
				<Head $compact={compact || viewport}>
					<div>
						<TitleRow>
							<h2>{title}</h2>
							{headerAction}
						</TitleRow>
						{description ? <p>{description}</p> : null}
					</div>
					<HeadButtons>
						{onMinimize ? (
							<WithTooltip text={minimizeTooltip}>
								<CloseButton
									type="button"
									aria-label={minimizeLabel}
									title={minimizeTooltip ? undefined : minimizeLabel}
									onClick={onMinimize}
								>
									<svg
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d="M6 12h12" />
									</svg>
								</CloseButton>
							</WithTooltip>
						) : null}
						<WithTooltip text={closeTooltip}>
							<CloseButton
								type="button"
								aria-label="Close"
								onClick={onClose}
							>
								<svg
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<path d="M6 6l12 12M18 6L6 18" />
								</svg>
							</CloseButton>
						</WithTooltip>
					</HeadButtons>
				</Head>
				{viewport ? <ViewportBody>{children}</ViewportBody> : children}
				{footer ? <Footer $compact={compact || viewport}>{footer}</Footer> : null}
			</Dialog>
		</Scrim>,
		document.body,
	);
};

const Scrim = styled.div`
	position: fixed;
	inset: 0;
	z-index: var(--z-modal);
	display: flex;
	align-items: flex-start;
	justify-content: center;
	padding: var(--space-8);
	overflow-y: auto;
	background: var(--color-scrim);
`;

const Dialog = styled.div<{ $compact: boolean; $viewport: boolean }>`
	width: 100%;
	max-width: ${({ $compact, $viewport }) => {
		if ($viewport) return "none";
		return $compact ? "var(--size-modal-width-compact)" : "var(--size-modal-width)";
	}};
	/* Vertically centred while it fits, top-anchored and scrollable once it does
	   not. An auto margin on a flex item absorbs the free space on both axes and
	   outranks the Scrim's align-items, so a short dialog sits in the middle of
	   the viewport; when the content grows past it there is no free space left,
	   the margins collapse to the Scrim's own padding, and nothing is cut off
	   the way centring via align-items alone would cut it. */
	margin: auto;
	padding: ${({ $compact, $viewport }) => ($compact || $viewport ? "var(--space-6)" : "var(--space-8)")};
	border-radius: var(--radius-lg);
	background: var(--color-surface);
	box-shadow: var(--shadow-modal);
	outline: none;

	/* viewport: the Scrim's padding is the only margin; the dialog takes the
	   whole height left, and ViewportBody the height under the title. */
	${({ $viewport }) =>
		$viewport &&
		`
		height: 100%;
		display: flex;
		flex-direction: column;
	`}
`;

const ViewportBody = styled.div`
	position: relative;
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
`;

const Head = styled.div<{ $compact: boolean }>`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: var(--space-4);
	margin-bottom: ${({ $compact }) => ($compact ? "var(--space-4)" : "var(--space-6)")};

	h2 {
		margin: 0;
		font-family: var(--font-family);
		font-size: var(--font-size-heading);
		font-weight: var(--font-weight-medium);
		line-height: 1.3;
		color: var(--color-text);
	}

	p {
		margin: var(--space-2) 0 0;
		font-family: var(--font-family);
		font-size: var(--font-size-body);
		line-height: var(--line-height-base);
		color: var(--color-text-secondary);
	}
`;

// Title and its action on one baseline. Centred rather than baseline-aligned:
// the action is a square control, and aligning its text-less box to a baseline
// leaves it sitting low against the heading.
const TitleRow = styled.div`
	display: flex;
	align-items: center;
	gap: var(--space-3);
`;

/* Wraps only when there is a text: Tooltip's inline trigger wrapper would
   otherwise sit around the close button of every dialog for nothing. */
const WithTooltip = ({ text, children }: { text?: React.ReactNode; children: React.ReactElement }) =>
	text ? <Tooltip text={text} position="bottom">{children}</Tooltip> : children;

const HeadButtons = styled.div`
	display: flex;
	flex: none;
	gap: var(--space-3);
`;

const CloseButton = styled.button`
	${iconSlot}
	flex: none;
	padding: 0;
	border: none;
	background: transparent;
	color: var(--color-text);
	cursor: pointer;
	${colorTransition}
	${focusRing}
`;

const Footer = styled.div<{ $compact: boolean }>`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: var(--space-2);
	margin-top: ${({ $compact }) => ($compact ? "var(--space-6)" : "var(--space-8)")};

	/* §8 Modal: the actions are auto-width, right-aligned. An hds Button is full
	   width by default, which in this row made two buttons split the dialog in
	   half - the rule is enforced here so no call site has to remember it. */
	> button {
		width: auto;
		flex: none;
		padding: 0 var(--space-5);
	}
`;

export default Modal;
